import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import useKeyDown from "../../../hooks/useKeyDown";
import useOutsideAlerter from "../../../hooks/useOutsideAlerter";
import { AppIconType } from "../AppIcon/AppIcon";
import AppInput, { AppInputProps } from "../AppInput/AppInput";
import AppText from "../AppText/AppText";

export interface AutoCompleteInputProps<V /*value type*/, S /* Suggestion type */>
	extends Omit<AppInputProps, "value" | "onChange" | "onChangeText" | "width"> {
	/**
	 * Array containing all suggestions for current search string.
	 */
	suggestions: S[];

	/**
	 * Callback invoked when search string updated.
	 * Should trigger search of suggestions.
	 */
	onSearch: (search?: string) => void;

	/**
	 * Invoked when a value is selected
	 */
	onChange?: (val?: V, suggestion?: S) => void;

	/**
	 * Current value.
	 */
	value?: V;

	/**
	 * Function that returns value from suggestion.
	 */
	getValue: (suggestion: S) => V;

	/**
	 * Function that should return display value of suggestion.
	 */
	getDisplayValue: (suggestion?: S) => string;

	/**
	 * Suggestions for initial value, needed to render a display value
	 */
	initialValueSuggestion?: S;

	renderSuggestion?: (suggestion: S) => React.ReactNode;

	icon?: AppIconType;

	// placeholder?: string; // TODO

	/**
	 * Extract key from suggestion, defaults to `s => s.id`
	 */
	keyExtractor?: (suggestion: S) => string;

	/**
	 * Minimum search string length needed to start doing searching, invoking BE.
	 */
	minChars?: number;

	width?: number | string;
}

function AutoCompleteInput<V, S = V>({
	type,
	suggestions,
	minChars = 2,
	width = 250,
	onSearch,
	onChange,
	value,
	getDisplayValue,
	getValue,
	initialValueSuggestion,
	renderSuggestion,
	icon = "search",
	// @ts-ignore
	keyExtractor = (suggestion: S) => suggestion?.id || "",
	...inputProps
}: AutoCompleteInputProps<V, S>) {
	const [searchStr, setSearchStr] = useState("");
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [focusedSearchHit, setFocusedSearchHit] = useState("");

	const inputRef = useRef<HTMLInputElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useKeyDown({
		onKeyDown: (e) => {
			const hasFocus = document.activeElement === inputRef.current;

			if (!hasFocus) return;

			const focusedIndex = suggestions.findIndex((item) => keyExtractor(item) === focusedSearchHit);

			if (e.key === "ArrowDown") {
				setFocusedSearchHit(
					keyExtractor(suggestions[Math.min(focusedIndex + 1, suggestions.length - 1)])
				);
			} else if (e.key === "ArrowUp") {
				setFocusedSearchHit(keyExtractor(suggestions[Math.max(focusedIndex - 1, 0)]));
			} else if (e.key === "Enter") {
				e.stopPropagation();
				e.preventDefault();

				if (!searchStr.trim()) {
					onChange && onChange(undefined);
				} else {
					handleSelect(Math.max(focusedIndex, 0));
				}
			} else if (e.key === "Escape") {
				setSearchStr("");
				setFocusedSearchHit("");
				handleOnChange("");
			}
		},
	});

	useOutsideAlerter(wrapperRef, () => setShowSuggestions(false));

	// Focus only re-opens suggestions — it must NOT go through handleOnChange,
	// whose empty-string branch clears the selected value. With a preselected
	// value (e.g. ?customer=<id> on the job form) the display text is still ""
	// while the suggestion loads, so an early focus (autoFocus) would wipe it.
	function handleFocus() {
		if (searchStr.length >= minChars) {
			setShowSuggestions(true);
			onSearch(searchStr);
		}
	}

	function handleOnChange(search: string) {
		search = search.trimStart();
		setSearchStr(search);

		if (search.length >= minChars) {
			setShowSuggestions(true);
			onSearch(search);
		} else {
			setShowSuggestions(false);
			onSearch(undefined);

			if (search.length === 0 && value) {
				onChange && onChange(undefined);
			}
		}
	}

	function handleSelect(index: number) {
		const selectedSuggestion = suggestions[index];
		setFocusedSearchHit("");

		if (selectedSuggestion) {
			onChange && onChange(getValue(selectedSuggestion), selectedSuggestion);
			setSearchStr(getDisplayValue(selectedSuggestion));
			setShowSuggestions(false);
		} else {
			onChange && onChange(undefined);
		}
	}

	useEffect(() => {
		if (value && !searchStr) {
			initialValueSuggestion && setSearchStr(getDisplayValue(initialValueSuggestion));
		}
	}, [value, initialValueSuggestion]);

	return (
		<Styles width={width} ref={wrapperRef}>
			<AppInput
				{...inputProps}
				onChangeText={handleOnChange}
				value={searchStr}
				containerMargin="0"
				icon={icon}
				onFocus={handleFocus}
				ref={inputRef}
			/>
			{showSuggestions && suggestions.length > 0 && (
				<ul className="suggestions">
					{suggestions.map((sugg, i) => (
						<li
							key={keyExtractor(sugg)}
							data-testid="autocomplete-suggestion"
							onClick={() => handleSelect(i)}
							className={
								focusedSearchHit && focusedSearchHit === keyExtractor(sugg)
									? "focused-suggestion"
									: ""
							}
						>
							{renderSuggestion ? (
								renderSuggestion(sugg)
							) : (
								<AppText>{getDisplayValue(sugg)}</AppText>
							)}
						</li>
					))}
				</ul>
			)}
		</Styles>
	);
}

export default AutoCompleteInput;

const Styles = styled.div<{ width: number | string }>`
	position: relative;
	width: ${(props) => (typeof props.width === "string" ? props.width : props.width + "px")};

	.suggestions {
		position: absolute;
		background: white;
		list-style: none;
		width: ${(props) => (typeof props.width === "string" ? props.width : props.width + "px")};
		z-index: 10;
		border: 1px solid ${(props) => props.theme.colors.COLOR_DIVIDER_GRAY};
		max-height: 400px;
		overflow-y: auto;

		li {
			padding: 0.75rem 0.5rem;
			display: block;
			cursor: pointer;
			border-bottom: 1px solid ${(props) => props.theme.colors.COLOR_DIVIDER_GRAY};

			&:last-child {
				border-bottom-style: none;
			}

			&:hover,
			&.focused-suggestion {
				background-color: ${(props) => props.theme.colors.COLOR_LIGHT_BLUE2};
			}

			&:first-child {
				font-weight: 700;
			}
		}
	}
`;
